// Queries the platform product catalog and maps each product to CatalogProduct (name, category,
// matching dimensions, properties, lengths, truck capacity) for the import's product matching,
// length validation and stock conversion.

import { useMemo } from 'react';
import { graphql } from '~/graphql';
import type { InventoryImportPlatformCatalogQuery as CatalogQueryResult } from '~/graphql/graphql';
import { useGraphqlQuery } from '~/hooks/useGraphqlQuery';
import { buildPropertySummary } from '~/pages/inventory-import/lib/catalog';
import type {
  CatalogLength,
  CatalogProduct,
} from '~/pages/inventory-import/lib/types';

const InventoryImportPlatformCatalogQuery = graphql(`
  query InventoryImportPlatformCatalog {
    platformProducts {
      id
      name
      category
      standardUnitsCode
      finishCode
      nominalThicknessInches
      nominalWidthInches
      actualThicknessInches
      actualWidthInches
      properties
      unitsPerTruckload
      lengths {
        id
        actualLengthInches
        nominalLengthInches
        displayValue
      }
    }
  }
`);

// Input shapes come straight from the generated query result — no hand-rolled duplicates.
export type RawProduct = NonNullable<
  CatalogQueryResult['platformProducts']
>[number];
type RawLength = NonNullable<NonNullable<RawProduct['lengths']>[number]>;

function toLength(l: RawLength): CatalogLength {
  return {
    id: l.id,
    actualLengthInches: l.actualLengthInches,
    nominalLengthInches: l.nominalLengthInches ?? null,
    displayValue: l.displayValue,
  };
}

// CatalogProduct/CatalogLength are the lib's normalized domain types (with searchName,
// propertySummary, nominal-only matching dims and sorted lengths) consumed by the matching and
// validation code — so this maps the raw query result into them rather than returning the raw
// PlatformProduct.
export function toCatalogProduct(p: RawProduct): CatalogProduct {
  const properties =
    p.properties && typeof p.properties === 'object'
      ? (p.properties as Record<string, unknown>)
      : {};
  const category = p.category;
  // Nominal-ONLY for matching (descriptors quote nominal, e.g. 2x4). Don't fall back to actual:
  // a "2x4" descriptor must not match a product whose actual 1.5×3.5 stood in for a missing
  // nominal — that would silently pair different sizes. A missing nominal simply doesn't match on
  // dimensions. Actual is kept separately below for display only (e.g. 1.5"×3.5").
  const thickness = p.nominalThicknessInches ?? null;
  const width = p.nominalWidthInches ?? null;
  const actualThickness =
    p.actualThicknessInches ?? p.nominalThicknessInches ?? null;
  const actualWidth = p.actualWidthInches ?? p.nominalWidthInches ?? null;
  const lengths = (p.lengths ?? [])
    .filter((l): l is RawLength => l !== null)
    .map(toLength)
    .sort((a, b) => a.actualLengthInches - b.actualLengthInches);

  return {
    id: p.id,
    name: p.name,
    category,
    standardUnitsCode: p.standardUnitsCode,
    thickness,
    width,
    properties,
    lengths,
    unitsPerTruckload: p.unitsPerTruckload ?? null,
    propertySummary: buildPropertySummary(
      category,
      properties,
      actualThickness,
      actualWidth,
      p.finishCode ?? null
    ),
    searchName: p.name.toLowerCase(),
  };
}

export function usePlatformCatalog() {
  const { data, isLoading, error } = useGraphqlQuery(
    InventoryImportPlatformCatalogQuery,
    ['inventory-import-platform-catalog']
  );

  const catalog = useMemo<CatalogProduct[]>(
    () => (data?.platformProducts ?? []).map(toCatalogProduct),
    [data]
  );

  return { catalog, isLoading, error };
}
